import { type ApiClient, isApiError } from "./client";
import type { AdminIdentity, AuthSession } from "./types";

export interface CurrentAdmin {
  session: AuthSession;
  identity: AdminIdentity;
  /** Whether the backend told us the admin's role and permissions (always true over HTTP now). */
  roleKnown: boolean;
}

export interface SessionApi {
  /** Admin session + admin identity; null when signed out. */
  current(): Promise<CurrentAdmin | null>;
  signIn(input: { email: string; password: string }): Promise<void>;
  signOut(): Promise<void>;
  /** Change your own password (replaces a temporary one); your other sessions are signed out. */
  changePassword(input: { currentPassword: string; newPassword: string }): Promise<void>;
  /** Your own profile: the Slack member id tagged in handoff alerts (null clears it). */
  updateProfile(input: { slackUserId: string | null }): Promise<void>;
}

/**
 * Platform-staff session. Admins sign in through the backend's separate admin auth
 * (/admin/auth, `rogue-admin` cookie); dealer accounts cannot sign in here.
 */
export function createHttpSessionApi(client: ApiClient): SessionApi {
  return {
    async current() {
      const session = await client.call<AuthSession | null>("getSession");
      if (!session?.user) return null;
      try {
        const identity = await client.call<AdminIdentity>("getAdminIdentity");
        return { session, identity, roleKnown: true };
      } catch (err) {
        // A session the admin API no longer accepts (e.g. account disabled) is signed out.
        if (isApiError(err) && (err.isUnauthenticated || err.isForbidden)) return null;
        throw err;
      }
    },
    async signIn({ email, password }) {
      await client.call("signInEmail", { body: { email: email.trim().toLowerCase(), password, rememberMe: true } });
    },
    async signOut() {
      await client.call("signOut", { body: {} });
    },
    async changePassword(input) {
      await client.call("changeOwnPassword", { body: input });
    },
    async updateProfile(input) {
      await client.call("updateAdminProfile", { body: input });
    },
  };
}
